import { PledgeBasedTarget, PledgeBasedProgress, PledgeBasedPlan, PledgeBasedWeeklyReport } from '@/types/pledgeBasedPlan';
import { Activity, AppState } from '@/types';

export interface MonthlyTargets {
  appointment_sets: number;
  opening_appointments: number;
  closing_appointments: number;
  lives_closed: number;
}

export interface AnnualTargets {
  appointment_sets: number;
  opening_interviews: number;
  closing_interviews: number;
  life_cases_needed: number;
}

export interface PledgeSheetData {
  minimumFyc: number;
  stretchedFyc: number;
  averageCaseSize: number;
  closingRate: number;
  openingToClosingRate: number;
  callToOpeningRate: number;
  monthlyTargets?: MonthlyTargets;
  annualTargets?: AnnualTargets;
  selectedMonths?: number; // 10, 12, etc.
}

/**
 * Calculate weekly targets from pledge sheet data.
 * If monthly_targets are provided in the pledge sheet, use them directly.
 * Otherwise, fall back to FYC-based calculation.
 */
export function calculateWeeklyTargetsFromPledgeSheet(
  pledgeData: PledgeSheetData,
  useStretchedGoals: boolean = false,
  conversionRatios?: { sets_per_opening: number; weeks_per_year: number }
): PledgeBasedTarget {
  // Calculate stretch factor based on FYC ratio
  // E.g., if stretched is 150K and minimum is 100K, factor = 1.5
  const stretchFactor = useStretchedGoals && pledgeData.stretchedFyc && pledgeData.minimumFyc && pledgeData.minimumFyc > 0
    ? pledgeData.stretchedFyc / pledgeData.minimumFyc
    : 1;

  // Priority 1: Use annual targets with selected months
  if (pledgeData.annualTargets && pledgeData.selectedMonths) {
    // Calculate working weeks based on selected months
    // 12 months = 52 weeks, 10 months = ~44 weeks, etc.
    const workingWeeks = Math.round(pledgeData.selectedMonths * (52 / 12));
    
    return {
      sets: Math.ceil((pledgeData.annualTargets.appointment_sets * stretchFactor) / workingWeeks),
      openings: Math.ceil((pledgeData.annualTargets.opening_interviews * stretchFactor) / workingWeeks),
      closings: Math.ceil((pledgeData.annualTargets.closing_interviews * stretchFactor) / workingWeeks),
      closed: Math.ceil((pledgeData.annualTargets.life_cases_needed * stretchFactor) / workingWeeks)
    };
  }

  // Priority 2: If we have pre-calculated monthly targets, use them directly
  if (pledgeData.monthlyTargets) {
    const weeksPerMonth = 4; // Use 4 weeks per month for simpler calculation
    return {
      sets: Math.ceil((pledgeData.monthlyTargets.appointment_sets * stretchFactor) / weeksPerMonth),
      openings: Math.ceil((pledgeData.monthlyTargets.opening_appointments * stretchFactor) / weeksPerMonth),
      closings: Math.ceil((pledgeData.monthlyTargets.closing_appointments * stretchFactor) / weeksPerMonth),
      closed: Math.ceil((pledgeData.monthlyTargets.lives_closed * stretchFactor) / weeksPerMonth)
    };
  }

  // Fallback: Calculate from FYC and conversion rates
  const fycGoal = useStretchedGoals ? pledgeData.stretchedFyc : pledgeData.minimumFyc;
  
  // Use provided ratios or defaults
  const setsPerOpening = conversionRatios?.sets_per_opening ?? 3;
  const weeksPerYear = conversionRatios?.weeks_per_year ?? 50;
  
  // Calculate annual targets
  const casesNeeded = Math.ceil(fycGoal / pledgeData.averageCaseSize);
  const closingAppointments = Math.ceil(casesNeeded / (pledgeData.closingRate / 100));
  const openingInterviews = Math.ceil(closingAppointments / (pledgeData.openingToClosingRate / 100));
  
  // Sets calculation using configurable ratio
  const setsNeeded = Math.ceil(openingInterviews * setsPerOpening);
  
  return {
    sets: Math.ceil(setsNeeded / weeksPerYear),
    openings: Math.ceil(openingInterviews / weeksPerYear),
    closings: Math.ceil(closingAppointments / weeksPerYear),
    closed: Math.ceil(casesNeeded / weeksPerYear)
  };
}

export function calculateCurrentProgress(
  activities: Activity[],
  weekStartDate: Date
): PledgeBasedProgress {
  const weekEndDate = new Date(weekStartDate);
  weekEndDate.setDate(weekEndDate.getDate() + 7);
  
  const thisWeekActivities = activities.filter(activity => {
    const activityDate = new Date(activity.timestamp);
    return activityDate >= weekStartDate && activityDate < weekEndDate;
  });
  
  const progress = {
    sets: 0,
    openings: 0,
    closings: 0,
    closed: 0
  };
  
  thisWeekActivities.forEach(activity => {
    // Normalize activity id from multiple possible sources
    let activityId = activity.activityType?.id?.toLowerCase();

    if (!activityId) {
      const name = (activity as any).name?.toString?.().toLowerCase?.();
      if (name) {
        if (name === 'set' || name === 'sets') activityId = 'set';
        else if (name === 'opening' || name === 'openings') activityId = 'opening';
        else if (name === 'closing' || name === 'closings') activityId = 'closing';
        else if (name === 'closed' || name === 'case closed') activityId = 'closed';
      }
    }

    if (!activityId && typeof (activity as any).points === 'number') {
      // Fallback by points mapping (kept in sync with activity points)
      const pts = Number((activity as any).points);
      if (pts === 1) activityId = 'set';
      else if (pts === 3) activityId = 'opening';
      else if (pts === 4) activityId = 'closing';
      else if (pts === 5) activityId = 'closed';
    }

    switch (activityId) {
      case 'set':
        progress.sets++;
        break;
      case 'opening':
        progress.openings++;
        break;
      case 'closing':
        progress.closings++;
        break;
      case 'closed':
        progress.closed++;
        break;
      default:
        // Unknown type; ignore silently to avoid noisy logs
        break;
    }
  });
  
  return progress;
}

export function calculateProgressPercentages(
  targets: PledgeBasedTarget,
  progress: PledgeBasedProgress
): { sets: number; openings: number; closings: number; closed: number; overall: number } {
  const percentages = {
    sets: targets.sets > 0 ? Math.round((progress.sets / targets.sets) * 100) : 0,
    openings: targets.openings > 0 ? Math.round((progress.openings / targets.openings) * 100) : 0,
    closings: targets.closings > 0 ? Math.round((progress.closings / targets.closings) * 100) : 0,
    closed: targets.closed > 0 ? Math.round((progress.closed / targets.closed) * 100) : 0,
    overall: 0 // Initialize overall
  };
  
  // Calculate overall percentage as average of ALL activities (not just non-zero)
  const allPercentages = [percentages.sets, percentages.openings, percentages.closings, percentages.closed];
  percentages.overall = Math.round(allPercentages.reduce((sum, p) => sum + p, 0) / 4);
  
  return percentages;
}

export function generateRecommendations(
  targets: PledgeBasedTarget,
  progress: PledgeBasedProgress,
  daysLeftInWeek: number
): string[] {
  const recommendations: string[] = [];
  
  // Calculate remaining work needed
  const remaining = {
    sets: Math.max(0, targets.sets - progress.sets),
    openings: Math.max(0, targets.openings - progress.openings),
    closings: Math.max(0, targets.closings - progress.closings),
    closed: Math.max(0, targets.closed - progress.closed)
  };
  
  // Calculate daily targets for remaining days (minimum 1 day)
  const workingDaysLeft = Math.max(1, daysLeftInWeek);
  
  // Priority-based recommendations
  if (remaining.sets > 0) {
    const dailySets = Math.ceil(remaining.sets / workingDaysLeft);
    if (daysLeftInWeek <= 1) {
      recommendations.push(`🚨 URGENT: Complete ${remaining.sets} sets today to meet your weekly target!`);
    } else {
      recommendations.push(`📞 Schedule ${dailySets} sets per day for the next ${workingDaysLeft} days (${remaining.sets} total remaining)`);
    }
  }
  
  if (remaining.openings > 0) {
    const dailyOpenings = Math.ceil(remaining.openings / workingDaysLeft);
    if (daysLeftInWeek <= 1) {
      recommendations.push(`🚨 URGENT: Secure ${remaining.openings} opening appointments today!`);
    } else {
      recommendations.push(`🎯 Focus on ${dailyOpenings} opening appointments daily (${remaining.openings} total needed)`);
    }
  }
  
  if (remaining.closings > 0) {
    const dailyClosings = Math.ceil(remaining.closings / workingDaysLeft);
    if (daysLeftInWeek <= 1) {
      recommendations.push(`🚨 URGENT: Book ${remaining.closings} closing appointments today!`);
    } else {
      recommendations.push(`💼 Aim for ${dailyClosings} closing appointments daily (${remaining.closings} total needed)`);
    }
  }
  
  if (remaining.closed > 0) {
    const dailyClosed = Math.ceil(remaining.closed / workingDaysLeft);
    if (daysLeftInWeek <= 1) {
      recommendations.push(`🚨 URGENT: Close ${remaining.closed} cases today to meet your target!`);
    } else {
      recommendations.push(`🏆 Work towards ${dailyClosed} closed cases daily (${remaining.closed} total needed)`);
    }
  }
  
  // Add strategic advice based on progress patterns
  if (progress.sets > 0 && remaining.openings > remaining.sets * 0.3) {
    recommendations.push(`💡 TIP: Your set-to-opening conversion needs attention. Focus on quality over quantity in your sets.`);
  }
  
  if (progress.openings > 0 && remaining.closings > remaining.openings * 0.7) {
    recommendations.push(`💡 TIP: Follow up on your opening appointments more effectively to secure closings.`);
  }
  
  if (progress.closings > 0 && remaining.closed > remaining.closings * 0.8) {
    recommendations.push(`💡 TIP: Focus on closing techniques - you have the appointments, now convert them!`);
  }
  
  // Motivational messages when on track
  if (recommendations.length === 0) {
    const totalProgress = (progress.sets + progress.openings + progress.closings + progress.closed);
    const totalTargets = (targets.sets + targets.openings + targets.closings + targets.closed);
    const overallProgress = totalTargets > 0 ? Math.round((totalProgress / totalTargets) * 100) : 100;
    
    if (overallProgress >= 100) {
      recommendations.push("🎉 Outstanding! You've exceeded all your weekly targets. Keep up the momentum!");
    } else if (overallProgress >= 90) {
      recommendations.push(`🔥 Excellent work! You're at ${overallProgress}% of your weekly targets.`);
    } else {
      recommendations.push(`✅ Great job! You're on track with all your targets this week (${overallProgress}% complete).`);
    }
  }
  
  return recommendations;
}

export function isOnTrackForWeek(
  targets: PledgeBasedTarget,
  progress: PledgeBasedProgress,
  dayOfWeek: number // 0 = Sunday, 6 = Saturday
): boolean {
  const expectedProgressRatio = (dayOfWeek + 1) / 7; // +1 because we include current day
  
  const actualProgressRatios = {
    sets: targets.sets > 0 ? progress.sets / targets.sets : 1,
    openings: targets.openings > 0 ? progress.openings / targets.openings : 1,
    closings: targets.closings > 0 ? progress.closings / targets.closings : 1,
    closed: targets.closed > 0 ? progress.closed / targets.closed : 1
  };
  
  // Consider on track if at least 75% of the expected progress is achieved across all activities
  const averageProgress = Object.values(actualProgressRatios).reduce((sum, ratio) => sum + ratio, 0) / 4;
  
  return averageProgress >= (expectedProgressRatio * 0.75);
}

/**
 * Get monthly targets directly from pledge sheet data
 */
export function getMonthlyTargetsFromPledgeSheet(
  pledgeData: PledgeSheetData
): PledgeBasedTarget {
  if (pledgeData.monthlyTargets) {
    return {
      sets: Math.ceil(pledgeData.monthlyTargets.appointment_sets ?? 0),
      openings: Math.ceil(pledgeData.monthlyTargets.opening_appointments ?? 0),
      closings: Math.ceil(pledgeData.monthlyTargets.closing_appointments ?? 0),
      closed: Math.ceil(pledgeData.monthlyTargets.lives_closed ?? 0)
    };
  }
  
  // Fallback: Calculate from weekly targets * 4
  const weeklyTargets = calculateWeeklyTargetsFromPledgeSheet(pledgeData, false);
  return {
    sets: weeklyTargets.sets * 4,
    openings: weeklyTargets.openings * 4,
    closings: weeklyTargets.closings * 4,
    closed: weeklyTargets.closed * 4
  };
}

/**
 * Calculate monthly progress from activities
 */
export function calculateMonthlyProgress(
  activities: Activity[],
  monthStartDate: Date
): PledgeBasedProgress {
  const monthEndDate = new Date(monthStartDate);
  monthEndDate.setMonth(monthEndDate.getMonth() + 1);
  
  const thisMonthActivities = activities.filter(activity => {
    const activityDate = new Date(activity.timestamp);
    return activityDate >= monthStartDate && activityDate < monthEndDate;
  });
  
  const progress = {
    sets: 0,
    openings: 0,
    closings: 0,
    closed: 0
  };
  
  thisMonthActivities.forEach(activity => {
    let activityId = activity.activityType?.id?.toLowerCase();

    if (!activityId) {
      const name = (activity as any).name?.toString?.().toLowerCase?.();
      if (name) {
        if (name === 'set' || name === 'sets') activityId = 'set';
        else if (name === 'opening' || name === 'openings') activityId = 'opening';
        else if (name === 'closing' || name === 'closings') activityId = 'closing';
        else if (name === 'closed' || name === 'case closed') activityId = 'closed';
      }
    }

    if (!activityId && typeof (activity as any).points === 'number') {
      const pts = Number((activity as any).points);
      if (pts === 1) activityId = 'set';
      else if (pts === 3) activityId = 'opening';
      else if (pts === 4) activityId = 'closing';
      else if (pts === 5) activityId = 'closed';
    }

    switch (activityId) {
      case 'set':
        progress.sets++;
        break;
      case 'opening':
        progress.openings++;
        break;
      case 'closing':
        progress.closings++;
        break;
      case 'closed':
        progress.closed++;
        break;
      default:
        break;
    }
  });
  
  return progress;
}

/**
 * Check if on track for monthly targets
 */
export function isOnTrackForMonth(
  targets: PledgeBasedTarget,
  progress: PledgeBasedProgress,
  dayOfMonth: number,
  daysInMonth: number
): boolean {
  const expectedProgressRatio = dayOfMonth / daysInMonth;
  
  const actualProgressRatios = {
    sets: targets.sets > 0 ? progress.sets / targets.sets : 1,
    openings: targets.openings > 0 ? progress.openings / targets.openings : 1,
    closings: targets.closings > 0 ? progress.closings / targets.closings : 1,
    closed: targets.closed > 0 ? progress.closed / targets.closed : 1
  };
  
  // Consider on track if at least 75% of the expected progress is achieved
  const averageProgress = Object.values(actualProgressRatios).reduce((sum, ratio) => sum + ratio, 0) / 4;
  
  return averageProgress >= (expectedProgressRatio * 0.75);
}

/**
 * Calculate weekly points needed based on pledge sheet data
 */
export function calculateWeeklyPointsFromPledgeSheet(
  pledgeData: PledgeSheetData, 
  useStretchedGoals: boolean = false,
  conversionRatios?: { sets_per_opening: number; weeks_per_year: number }
): { totalPoints: number; breakdown: PledgeBasedTarget & { points: Record<keyof PledgeBasedTarget, number> } } {
  const targets = calculateWeeklyTargetsFromPledgeSheet(pledgeData, useStretchedGoals, conversionRatios);
  
  // Activity points mapping - matches the points in activities.ts
  const activityPoints = {
    sets: 1,
    openings: 3,
    closings: 4,
    closed: 5,
  };
  
  const points = {
    sets: targets.sets * activityPoints.sets,
    openings: targets.openings * activityPoints.openings,
    closings: targets.closings * activityPoints.closings,
    closed: targets.closed * activityPoints.closed,
  };
  
  const totalPoints = points.sets + points.openings + points.closings + points.closed;
  
  return {
    totalPoints,
    breakdown: {
      ...targets,
      points
    }
  };
}

/**
 * Calculate daily targets from weekly pledge targets
 */
export function calculateDailyTargetsFromWeekly(
  weeklyTargets: PledgeBasedTarget,
  workingDaysPerWeek: number = 5
): PledgeBasedTarget {
  return {
    sets: Math.ceil(weeklyTargets.sets / workingDaysPerWeek),
    openings: Math.ceil(weeklyTargets.openings / workingDaysPerWeek),
    closings: Math.ceil(weeklyTargets.closings / workingDaysPerWeek),
    closed: Math.ceil(weeklyTargets.closed / workingDaysPerWeek)
  };
}

/**
 * Calculate today's progress from activities for today only
 */
export function calculateTodayProgress(activities: Activity[]): PledgeBasedProgress {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  
  const todayActivities = activities.filter(activity => {
    const activityDate = new Date(activity.timestamp);
    return activityDate >= today && activityDate < tomorrow;
  });
  
  const progress = {
    sets: 0,
    openings: 0,
    closings: 0,
    closed: 0
  };
  
  todayActivities.forEach(activity => {
    let activityId = activity.activityType?.id?.toLowerCase();

    if (!activityId) {
      const name = (activity as any).name?.toString?.().toLowerCase?.();
      if (name) {
        if (name === 'set' || name === 'sets') activityId = 'set';
        else if (name === 'opening' || name === 'openings') activityId = 'opening';
        else if (name === 'closing' || name === 'closings') activityId = 'closing';
        else if (name === 'closed' || name === 'case closed') activityId = 'closed';
      }
    }

    if (!activityId && typeof (activity as any).points === 'number') {
      const pts = Number((activity as any).points);
      if (pts === 1) activityId = 'set';
      else if (pts === 3) activityId = 'opening';
      else if (pts === 4) activityId = 'closing';
      else if (pts === 5) activityId = 'closed';
    }

    switch (activityId) {
      case 'set':
        progress.sets++;
        break;
      case 'opening':
        progress.openings++;
        break;
      case 'closing':
        progress.closings++;
        break;
      case 'closed':
        progress.closed++;
        break;
      default:
        break;
    }
  });
  
  return progress;
}